import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { crmApi, errCode, errMsg } from '@crm/api';
import { TrialDateModal } from '@crm/components/TrialDateModal';
import { hasCrmPermission } from '@crm/permissions';
import {
  UiAutosuggest,
  UiButton,
  UiCheckbox,
  UiInput,
  UiNativeSelect,
} from '@shared/common/ui';
import {
  describeTrialDate,
  formatTrialDateShort,
  isAwaitingTrial,
  isoWeekRange,
  localIsoToday,
  shiftIsoDate,
  TRIAL_DATE_CHANGE_TYPE_LABELS,
  TRIAL_DATE_ERROR_LABELS,
} from '@crm/utils/trialDate';

function formatPhoneRu(phone) {
  if (!phone) return null;
  const d = String(phone).replace(/\D/g, '');
  if (d.length === 11 && d.startsWith('7')) {
    return `+7 (${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7, 9)}-${d.slice(9, 11)}`;
  }
  return phone;
}

function plural(n, one, few, many) {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
  return many;
}

function formatDaysInStatus(days) {
  if (days === null || days === undefined) return null;
  if (days === 0) return 'сегодня';
  return `${days} ${plural(days, 'день', 'дня', 'дней')} в статусе`;
}

const SOURCE_LABEL = {
  manual: 'вручную',
  automation: 'автоматизация',
  import: 'импорт',
};

function HistoryModal({ branchId, card, onClose }) {
  const [items, setItems] = useState([]);
  const [dateItems, setDateItems] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const api = crmApi(branchId);
    Promise.all([
      api.get(`/funnel/participations/${card.clientDirectionId}/history`),
      api.get(`/funnel/participations/${card.clientDirectionId}/trial-date-history`),
    ])
      .then(([status, dates]) => {
        if (!alive) return;
        setItems(status.data.items ?? []);
        setDateItems(dates.data.items ?? []);
      })
      .catch((e) => alive && setError(errMsg(e, 'Не удалось загрузить историю')))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [branchId, card.clientDirectionId]);

  return (
    <div className="modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="modal-card modal-card--wide"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="funnel-history-title"
      >
        <header className="modal-card__header">
          <h2 id="funnel-history-title">История участия</h2>
          <UiButton variant="ghost" size="sm" type="button" onClick={onClose}>
            Закрыть
          </UiButton>
        </header>
        <div className="modal-card__body">
          <p className="muted" style={{ marginTop: 0 }}>
            {card.fio} · {card.directionName ?? 'без направления'}
          </p>

          {loading && <div className="placeholder">Загрузка…</div>}
          {error && <div className="banner banner--danger">{error}</div>}

          {!loading && !error && (
            <>
              <h3 className="sales-funnel__section-title">Статусы</h3>
              {items.length === 0 ? (
                <div className="placeholder">Переходов пока не было</div>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Когда</th>
                      <th>Было</th>
                      <th>Стало</th>
                      <th>Источник</th>
                      <th>Кто</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it) => (
                      <tr key={it.id}>
                        <td>{it.changedAt ? it.changedAt.slice(0, 16).replace('T', ' ') : '—'}</td>
                        <td className="muted">{it.oldStatusName ?? '—'}</td>
                        <td>{it.newStatusName ?? '—'}</td>
                        <td className="muted">{SOURCE_LABEL[it.source] ?? it.source}</td>
                        <td className="muted">{it.changedByName ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              <h3 className="sales-funnel__section-title">Дата пробного</h3>
              {dateItems.length === 0 ? (
                <div className="placeholder">Дата пробного не назначалась</div>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Когда</th>
                      <th>Было</th>
                      <th>Стало</th>
                      <th>Тип</th>
                      <th>Кто</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dateItems.map((it) => (
                      <tr key={it.id}>
                        <td>{it.changedAt ? it.changedAt.slice(0, 16).replace('T', ' ') : '—'}</td>
                        <td className="muted">{formatTrialDateShort(it.oldDate)}</td>
                        <td>{formatTrialDateShort(it.newDate)}</td>
                        <td className="muted">
                          {TRIAL_DATE_CHANGE_TYPE_LABELS[it.changeType] ?? it.changeType}
                        </td>
                        <td className="muted">{it.changedByName ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Рабочая доска: одна карточка = одно участие клиента в направлении (client_direction).
 * Клиент с двумя направлениями честно стоит в двух колонках — это разные сделки.
 */
const TRIAL_FILTER_OPTIONS = [
  { value: '', label: 'Любая' },
  { value: 'today', label: 'Сегодня' },
  { value: 'tomorrow', label: 'Завтра' },
  { value: 'week', label: 'Эта неделя' },
  { value: 'custom', label: 'Выбрать период' },
];

/** Пресет фильтра «Дата пробного» → границы периода (ТЗ §11). */
function resolveTrialDateRange(preset, today, custom) {
  if (!today) return null;
  if (preset === 'today') return { from: today, to: today };
  if (preset === 'tomorrow') {
    const next = shiftIsoDate(today, 1);
    return { from: next, to: next };
  }
  if (preset === 'week') return isoWeekRange(today);
  if (preset === 'custom' && (custom.from || custom.to)) {
    return { from: custom.from || null, to: custom.to || null };
  }
  return null;
}

export function SalesFunnelBoard() {
  const { ownerId, branchId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { profile, branches } = useSelector((s) => s.crmAuth);
  const canMove = hasCrmPermission(profile, branches, branchId, 'funnel.card.status.update');
  const canOpenClient = hasCrmPermission(profile, branches, branchId, 'client.detail.view');

  const [board, setBoard] = useState({
    columns: [],
    automationWarnings: [],
    stagesConfigured: true,
  });
  // Стартуем от часов браузера, затем уточняем датой владельца из ответа backend.
  const [today, setToday] = useState(localIsoToday);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [moveError, setMoveError] = useState(null);

  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [includeClosed, setIncludeClosed] = useState(false);
  const [direction, setDirection] = useState(null);
  const [dirQuery, setDirQuery] = useState('');
  const [dirSuggestions, setDirSuggestions] = useState([]);

  // ?trialDate=today — ссылка «Все пробные →» из виджета главной страницы (ТЗ §33).
  const [trialPreset, setTrialPreset] = useState(() =>
    TRIAL_FILTER_OPTIONS.some((it) => it.value === searchParams.get('trialDate'))
      ? searchParams.get('trialDate')
      : '',
  );
  const [trialCustom, setTrialCustom] = useState({ from: '', to: '' });
  // Фильтр по дате применяется сразу, чтобы не «терять» его в кнопке Применить.
  const [appliedTrial, setAppliedTrial] = useState({ preset: trialPreset, custom: trialCustom });

  const [dragCardId, setDragCardId] = useState(null);
  const [dropColumnKey, setDropColumnKey] = useState(null);
  const [historyCard, setHistoryCard] = useState(null);
  const [trialRequest, setTrialRequest] = useState(null);
  const [trialBusy, setTrialBusy] = useState(false);
  const [trialError, setTrialError] = useState(null);

  const fetchBoard = useCallback(async () => {
    if (!branchId) return;
    setLoading(true);
    setError(null);
    const params = {};
    if (appliedSearch.trim()) params.q = appliedSearch.trim();
    if (direction?.id) params.directionId = direction.id;
    if (includeClosed) params.includeClosed = '1';
    const range = resolveTrialDateRange(appliedTrial.preset, today, appliedTrial.custom);
    if (range?.from) params.trialDateFrom = range.from;
    if (range?.to) params.trialDateTo = range.to;
    try {
      const { data } = await crmApi(branchId).get('/funnel/board', { params });
      setBoard(data);
      if (data.today) setToday((prev) => (prev === data.today ? prev : data.today));
    } catch (e) {
      setError(errMsg(e, 'Не удалось загрузить воронку'));
    } finally {
      setLoading(false);
    }
  }, [branchId, appliedSearch, direction, includeClosed, appliedTrial, today]);

  useEffect(() => {
    fetchBoard();
  }, [fetchBoard]);

  useEffect(() => {
    if (!dirQuery.trim() || !branchId) {
      setDirSuggestions([]);
      return undefined;
    }
    const t = setTimeout(() => {
      crmApi(branchId)
        .get('/directions', { params: { q: dirQuery.trim(), limit: 30 } })
        .then(({ data }) => setDirSuggestions(data.items ?? []))
        .catch(() => setDirSuggestions([]));
    }, 250);
    return () => clearTimeout(t);
  }, [dirQuery, branchId]);

  const cardsById = useMemo(() => {
    const map = new Map();
    for (const column of board.columns ?? []) {
      for (const card of column.items) map.set(card.id, { card, columnKey: column.key });
    }
    return map;
  }, [board]);

  /** Одна точка отправки статуса: и обычный DnD, и подтверждение даты в модалке. */
  const submitMove = useCallback(
    async (card, columnKey, trialLessonDate) => {
      const body = { status: columnKey };
      if (trialLessonDate !== undefined) body.trialLessonDate = trialLessonDate;
      await crmApi(branchId).patch(
        `/funnel/participations/${card.clientDirectionId}/status`,
        body,
      );
      await fetchBoard();
    },
    [branchId, fetchBoard],
  );

  const onDrop = async (column) => {
    setDropColumnKey(null);
    const entry = dragCardId ? cardsById.get(dragCardId) : null;
    setDragCardId(null);
    if (!entry || entry.columnKey === column.key) return;
    if (!entry.card.clientDirectionId) return;

    setMoveError(null);

    // Колонка требует дату — сначала модалка, карточка переезжает только после ответа (ТЗ §9).
    if (column.trialDateMode && column.trialDateMode !== 'none') {
      setTrialError(null);
      setTrialRequest({
        card: entry.card,
        columnKey: column.key,
        columnName: column.name,
        mode: column.trialDateMode,
      });
      return;
    }

    try {
      await submitMove(entry.card, column.key);
    } catch (e) {
      setMoveError(errMsg(e, 'Не удалось изменить статус'));
    }
  };

  const confirmTrialDate = async (date) => {
    if (!trialRequest) return;
    setTrialBusy(true);
    setTrialError(null);
    try {
      await submitMove(trialRequest.card, trialRequest.columnKey, date);
      setTrialRequest(null);
    } catch (e) {
      // Дублируем проверки backend: он остаётся источником правды (ТЗ §44.10).
      setTrialError(
        TRIAL_DATE_ERROR_LABELS[errCode(e)] ?? errMsg(e, 'Не удалось изменить статус'),
      );
    } finally {
      setTrialBusy(false);
    }
  };

  const applySearch = (e) => {
    e.preventDefault();
    setAppliedSearch(search);
    setAppliedTrial({ preset: trialPreset, custom: trialCustom });
  };

  const onTrialPresetChange = (value) => {
    setTrialPreset(value);
    if (value !== 'custom') {
      setAppliedTrial({ preset: value, custom: trialCustom });
      const next = new URLSearchParams(searchParams);
      if (value) next.set('trialDate', value);
      else next.delete('trialDate');
      setSearchParams(next, { replace: true });
    }
  };

  const statusColumns = (board.columns ?? []).filter((it) => it.kind === 'status');

  return (
    <div className="sales-funnel">
      <form className="sales-funnel__filters" onSubmit={applySearch}>
        <UiInput
          label="ФИО или телефон"
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <UiAutosuggest
          label="Направление"
          value={direction ? direction.name : dirQuery}
          onChange={(v) => {
            setDirQuery(v);
            setDirection(null);
          }}
          suggestions={dirSuggestions}
          getOptionLabel={(it) => it.name}
          getOptionKey={(it) => it.id}
          onSelect={(it) => {
            setDirection(it);
            setDirQuery('');
          }}
          clearable
          onClear={() => {
            setDirection(null);
            setDirQuery('');
          }}
          placeholder="Все направления"
        />
        <UiNativeSelect
          label="Дата пробного"
          value={trialPreset}
          onChange={(e) => onTrialPresetChange(e.target.value)}
        >
          {TRIAL_FILTER_OPTIONS.map((it) => (
            <option key={it.value} value={it.value}>
              {it.label}
            </option>
          ))}
        </UiNativeSelect>
        {trialPreset === 'custom' && (
          <>
            <UiInput
              label="Пробное с"
              type="date"
              value={trialCustom.from}
              onChange={(e) => setTrialCustom((s) => ({ ...s, from: e.target.value }))}
            />
            <UiInput
              label="Пробное по"
              type="date"
              value={trialCustom.to}
              onChange={(e) => setTrialCustom((s) => ({ ...s, to: e.target.value }))}
            />
          </>
        )}
        <UiCheckbox
          label="Показать закрытые"
          title="Участия, по которым занятия прекращены"
          checked={includeClosed}
          onChange={(e) => setIncludeClosed(e.target.checked)}
        />
        <UiButton type="submit">Применить</UiButton>
      </form>

      {board.automationWarnings?.length > 0 && (
        <div className="banner banner--danger">
          ⚠ Автоматизация требует настройки: целевой статус недоступен —{' '}
          {board.automationWarnings.map((it) => `«${it.name}»`).join(', ')}. Откройте настройки
          владельца → Воронка продаж → Автоматизации.
        </div>
      )}

      {moveError && <div className="banner banner--danger">{moveError}</div>}
      {error && <div className="banner banner--danger">{error}</div>}

      {!loading && !board.stagesConfigured && (
        <div className="placeholder">
          Ни один статус не включён как этап воронки. Настройки владельца → Воронка продаж →
          Этапы.
        </div>
      )}

      {loading ? (
        <div className="placeholder">Загрузка…</div>
      ) : (
        <div className="sales-funnel__board">
          {(board.columns ?? []).map((column) => (
            <section
              key={column.key}
              className={`sales-funnel__column${
                dropColumnKey === column.key ? ' is-drop-target' : ''
              }${column.kind === 'inbox' ? ' sales-funnel__column--inbox' : ''}`}
              onDragOver={(e) => {
                if (!canMove || column.kind === 'inbox') return;
                e.preventDefault();
                setDropColumnKey(column.key);
              }}
              onDragLeave={() => setDropColumnKey((k) => (k === column.key ? null : k))}
              onDrop={(e) => {
                e.preventDefault();
                if (!canMove || column.kind === 'inbox') return;
                onDrop(column);
              }}
            >
              <header className="sales-funnel__column-head">
                <span
                  className="sales-funnel__dot"
                  style={{ background: column.color ?? '#c7cbd4' }}
                />
                <h3>{column.name}</h3>
                <span className="sales-funnel__count">{column.total}</span>
              </header>

              {column.items.length === 0 ? (
                <p className="sales-funnel__empty">Пусто</p>
              ) : (
                column.items.map((card) => {
                  const trial = describeTrialDate(card.trialLessonDate, today);
                  // Просрочка — проблема только пока клиент ещё ждёт пробного.
                  const trialTone =
                    trial?.tone === 'overdue' && !isAwaitingTrial(card.systemType)
                      ? 'planned'
                      : trial?.tone;
                  return (
                  <article
                    key={card.id}
                    className={`sales-funnel__card${
                      dragCardId === card.id ? ' is-dragging' : ''
                    }${card.isActive ? '' : ' is-closed'}`}
                    draggable={canMove && card.kind === 'participation'}
                    onDragStart={() => setDragCardId(card.id)}
                    onDragEnd={() => setDragCardId(null)}
                  >
                    <div className="sales-funnel__card-title">
                      {canOpenClient ? (
                        <Link to={`/${ownerId}/crm/${branchId}/clients/${card.clientId}`}>
                          {card.fio ?? 'Без имени'}
                        </Link>
                      ) : (
                        (card.fio ?? 'Без имени')
                      )}
                    </div>

                    <div className="sales-funnel__card-meta">
                      {card.directionName ?? 'Направление не выбрано'}
                    </div>

                    {card.phone && (
                      <a className="sales-funnel__card-phone" href={`tel:${card.phone}`}>
                        {formatPhoneRu(card.phone)}
                      </a>
                    )}

                    {card.parentFio && (
                      <div className="sales-funnel__card-meta">
                        Родитель: {card.parentFio}
                        {card.parentPhone ? ` · ${formatPhoneRu(card.parentPhone)}` : ''}
                      </div>
                    )}

                    {trial && (
                      <div className={`sales-funnel__trial sales-funnel__trial--${trialTone}`}>
                        {trialTone === 'overdue' ? `⚠ ${trial.text}` : trial.text}
                      </div>
                    )}

                    <div className="sales-funnel__card-footer">
                      <span className="muted">{formatDaysInStatus(card.daysInStatus) ?? '—'}</span>
                      {card.kind === 'participation' && (
                        <button
                          type="button"
                          className="sales-funnel__link"
                          onClick={() => setHistoryCard(card)}
                        >
                          История
                        </button>
                      )}
                    </div>

                    {!card.isActive && <div className="sales-funnel__badge">Закрыто</div>}
                    {card.kind === 'inbox' && (
                      <div className="sales-funnel__badge">Нет направления</div>
                    )}
                  </article>
                  );
                })
              )}

              {column.items.length < column.total && (
                <p className="sales-funnel__empty">
                  Показаны первые {column.items.length} из {column.total}. Уточните фильтр.
                </p>
              )}
            </section>
          ))}

          {statusColumns.length === 0 && board.stagesConfigured && (
            <div className="placeholder">Нет участий, подходящих под фильтр</div>
          )}
        </div>
      )}

      {historyCard && (
        <HistoryModal
          branchId={branchId}
          card={historyCard}
          onClose={() => setHistoryCard(null)}
        />
      )}

      {trialRequest && (
        <TrialDateModal
          mode={trialRequest.mode}
          currentDate={trialRequest.card.trialLessonDate}
          subtitle={`${trialRequest.card.fio} · ${trialRequest.card.directionName ?? 'без направления'} → ${trialRequest.columnName}`}
          submitLabel={trialRequest.mode === 'reschedule_required' ? 'Перенести' : 'Перевести'}
          today={today}
          busy={trialBusy}
          error={trialError}
          onSubmit={confirmTrialDate}
          onClose={() => {
            setTrialRequest(null);
            setTrialError(null);
          }}
        />
      )}
    </div>
  );
}
